import { documentationClient } from 'shared/domain/documentation/documentationClient';
import type { StateCreator } from 'zustand';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import type { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import { logError } from 'setup/logger/logError';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { normalizeForSearch } from 'shared/utils/search';
import { debounce } from 'shared/utils/debounce';
import { deepEqual } from 'shared/utils/deepEqual';
import { DocumentState } from 'components/common/dataGrid/columnsFactory/types';
import type { FiltersSlice } from './types';
import type { DocumentationViewItem } from 'store/documentation/model/types';
import { buildFilteredDirectoryRequest } from '../data/model';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import { createProjectChangeCheck } from '../moduleLifecycle/model';
import type { DocumentationStoreSlices } from '../types';
import {
  FILTER_OPTIONS,
  INITIAL_FILTERS_STATE,
  applySorting,
  buildViewRows,
  MINIMAL_VALUE_GETTERS,
  DEFAULT_FILTER_MODEL,
  DEFAULT_SORT_MODEL,
  getDocumentationSearchQuery,
  isSearchActive,
} from './model';

export * from './types';
export { DEFAULT_FILTER_MODEL, DEFAULT_SORT_MODEL };

const DOCUMENTATION_SEARCH_STORAGE_KEY = 'documentation';
const SEARCH_PHRASE_DEBOUNCE_MS = 300;
const SEARCH_RESULTS_REFRESH_DEBOUNCE_MS = 500;

type FiltersSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  FiltersSlice
>;

export const createFiltersSlice: FiltersSliceCreator = (set, get) => {
  // Per-store async bookkeeping - not rendered, so kept out of store state.
  let searchPhraseDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  // Only the latest request may write; older responses are dropped.
  let latestSearchRequestId = 0;
  let latestDirectoryRowsRequestId = 0;
  // Functions need no rendering - kept out of state; set by the grid.
  let sortValueGetters: DocumentationValueGetters = MINIMAL_VALUE_GETTERS;

  const sortRows = (
    rows: DocumentationViewItem[],
    sortModel: GridSortModel
  ): DocumentationViewItem[] => applySorting(rows, sortModel, sortValueGetters);

  const cancelSearchPhraseSynchronization = (): void => {
    if (searchPhraseDebounceTimeout) {
      clearTimeout(searchPhraseDebounceTimeout);
      searchPhraseDebounceTimeout = null;
    }
  };

  const scheduleSearchPhraseSynchronization = (phrase: string): void => {
    cancelSearchPhraseSynchronization();
    searchPhraseDebounceTimeout = setTimeout(() => {
      searchPhraseDebounceTimeout = null;
      void get().searchDocumentations(phrase);
    }, SEARCH_PHRASE_DEBOUNCE_MS);
  };

  // Coalesces bursts of document/directory changes (edit, upload, archive,
  // folder rename - possibly from both channels) into one silent re-search.
  const refreshSearchResultsDebounced = debounce((): void => {
    const state = get();
    if (!isSearchActive(state)) {
      return;
    }
    void state.searchDocumentations(state.searchPhrase, { silent: true });
  }, SEARCH_RESULTS_REFRESH_DEBOUNCE_MS);

  return {
    ...INITIAL_FILTERS_STATE,

    initializeSearchPhrase: (projectId: string): void => {
      if (!projectId) {
        cancelSearchPhraseSynchronization();
        set({ searchProjectId: '', searchPhrase: '', isLoadingSearch: false });
        return;
      }

      const phrase = LocalStorageManager.getSearchPhrase(projectId, DOCUMENTATION_SEARCH_STORAGE_KEY) || '';
      set({
        searchProjectId: projectId,
        searchPhrase: phrase,
        isLoadingSearch: isSearchActive({ searchPhrase: phrase }),
      });
      scheduleSearchPhraseSynchronization(phrase);
    },

    setFilterModel: (filterModel: GridFilterModel): void => {
      set({
        filterModel: {
          ...filterModel,
          items: (filterModel.items ?? []).filter(
            (item) => item.field !== FILTER_OPTIONS.fixedFilterField
          ),
        },
      });
    },

    setDocumentState: (documentState: DocumentState): void => {
      set({ documentState });
    },

    // A sort change re-sorts the current rows locally - no service worker request.
    setSortModel: (sortModel: GridSortModel): void => {
      if (deepEqual(sortModel, get().sortModel)) {
        return;
      }
      set({ sortModel, filteredRows: sortRows(get().baseRows, sortModel) });
    },

    setSortValueGetters: (valueGetters): void => {
      if (valueGetters === sortValueGetters) {
        return;
      }
      sortValueGetters = valueGetters;
      set({ filteredRows: sortRows(get().baseRows, get().sortModel) });
    },

    commitVisibleRows: (rows): void => {
      set({ baseRows: rows, filteredRows: sortRows(rows, get().sortModel) });
    },

    setSearchPhrase: (phrase: string): void => {
      const { searchProjectId } = get();

      if (searchProjectId) {
        LocalStorageManager.setSearchPhrase(searchProjectId, DOCUMENTATION_SEARCH_STORAGE_KEY, phrase);
      }

      set({
        searchPhrase: phrase,
        isLoadingSearch: isSearchActive({ searchPhrase: phrase }),
      });
      scheduleSearchPhraseSynchronization(phrase);
    },

    searchDocumentations: async (phrase, options): Promise<void> => {
      const requestId = ++latestSearchRequestId;
      const hasProjectChanged = createProjectChangeCheck(get);
      const isLatestRequest = (): boolean =>
        requestId === latestSearchRequestId && !hasProjectChanged();
      const query = getDocumentationSearchQuery(phrase);

      if (!query) {
        set({
          searchedDirectories: [],
          searchedDocumentations: [],
          isLoadingSearch: false,
        });
        return;
      }

      if (!options?.silent) {
        set({ isLoadingSearch: true });
      }

      try {
        const { allDirectories } = get();

        const searchedDirectories = allDirectories.filter((directory: DirectoryModel) =>
          normalizeForSearch(directory.name).includes(query)
        );

        const searchedDocumentations = await documentationClient.search(
          query,
          { skipResync: options?.silent }
        );

        const currentQuery = getDocumentationSearchQuery(get().searchPhrase);
        if (!isLatestRequest() || currentQuery !== query) {
          return;
        }

        set({
          searchedDirectories,
          searchedDocumentations,
          isLoadingSearch: false,
        });
      } catch (error) {
        logChannelRequestFailure('Error searching documentations', error, { phrase: query });
        // Also clears a loading state left by a request this one superseded.
        if (isLatestRequest()) {
          set({ isLoadingSearch: false });
        }
      }
    },

    scheduleSearchResultsRefresh: (): void => {
      refreshSearchResultsDebounced();
    },

    applyFilters: (): void => {
      const state = get();
      const {
        filterModel,
        documentState,
        searchedDirectories,
        searchedDocumentations,
        isLoadingCurrentDirDirectories,
        isLoadingCurrentDirDocumentations,
        isLoadingSearch,
      } = state;

      const searchActive = isSearchActive(state);
      // Search results come from a separate source, so in search mode wait only
      // for them - directory loading must not block applying search results.
      const isDataLoading = searchActive
        ? isLoadingSearch
        : isLoadingCurrentDirDirectories || isLoadingCurrentDirDocumentations;

      if (isDataLoading) {
        return;
      }

      // Search mode: filter locally (search results come from separate endpoint)
      if (searchActive) {
        set({ isFiltering: true });

        try {
          const rows = buildViewRows({
            directories: searchedDirectories,
            documentations: searchedDocumentations,
            documentState,
          });
          get().commitVisibleRows(rows);
          set({ isFiltering: false });
        } catch (error) {
          logError('Failed to filter search results', error);
          get().commitVisibleRows([]);
          set({ isFiltering: false });
        }
        return;
      }

      // Directory view: SW handles all filtering
      const requestId = ++latestDirectoryRowsRequestId;
      const hasProjectChanged = createProjectChangeCheck(get);
      const isLatestRequest = (): boolean =>
        requestId === latestDirectoryRowsRequestId && !hasProjectChanged();
      const parentId = state.currentDirectory;
      set({ isFiltering: true });

      documentationClient.getFilteredDirectory(
        buildFilteredDirectoryRequest({ parentId, filterModel, documentState })
      )
        .then(({ directories, documentations }) => {
          // A newer request owns the rows (and the filtering flag).
          if (!isLatestRequest()) {
            return;
          }
          // The user started a search or opened another folder meanwhile.
          const current = get();
          if (isSearchActive(current) || !areDirectoryIdsEqual(current.currentDirectory, parentId)) {
            set({ isFiltering: false });
            return;
          }
          get().commitVisibleRows(buildViewRows({ directories, documentations, documentState }));
          set({ isFiltering: false });
        })
        .catch((error) => {
          logChannelRequestFailure('Failed to fetch directory view from service worker', error);
          if (isLatestRequest()) {
            set({ isFiltering: false });
          }
        });
    },

    resetFiltersState: (): void => {
      cancelSearchPhraseSynchronization();
      refreshSearchResultsDebounced.cancel();
      set(INITIAL_FILTERS_STATE);
    },
  };
};
